import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Mail, Search, RefreshCw, XCircle, CheckCircle2, Clock, AlertTriangle, Ban } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin → Email drip queue.
 *
 * Read/write view of every Day-2 and Day-5 follow-up email that has been
 * scheduled by `/api/check-name/pdf`. Lets Sophie see what's pending, what's
 * been sent, and cancel any drip before it goes out (e.g. customer phoned in).
 *
 * Backend endpoints:
 *   GET  /api/admin/email-drips?status=pending|sent|skipped|failed
 *   POST /api/admin/email-drips/{id}/cancel
 */
export default function AdminEmailDrips() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  const fetchDrips = async (signal) => {
    const params = statusFilter === "all" ? {} : { status_filter: statusFilter };
    const r = await api.get("/admin/email-drips", { params, signal });
    return r.data || [];
  };

  const load = async () => {
    setLoading(true);
    try {
      setItems(await fetchDrips());
    } catch (e) {
      toast.error("Couldn't load drip queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const data = await fetchDrips();
        if (!cancelled) setItems(data);
      } catch (e) {
        if (!cancelled) toast.error("Couldn't load drip queue");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [statusFilter]);

  const cancelDrip = async (id, email) => {
    if (!window.confirm(`Cancel pending drip to ${email}? They won't get this nudge.`)) return;
    try {
      await api.post(`/admin/email-drips/${id}/cancel`);
      toast.success("Drip cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const filtered = useMemo(() => items.filter((d) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      (d.lead_email || "").toLowerCase().includes(needle) ||
      (d.business_name || "").toLowerCase().includes(needle) ||
      (d.kind || "").toLowerCase().includes(needle) ||
      (d.industry_label || "").toLowerCase().includes(needle)
    );
  }), [items, q]);

  const counts = useMemo(() => {
    const c = { pending: 0, sent: 0, skipped: 0, failed: 0 };
    items.forEach((d) => { if (c[d.status] !== undefined) c[d.status] += 1; });
    return c;
  }, [items]);

  const statusBadge = (s) => {
    if (s === "sent") return { cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 };
    if (s === "pending") return { cls: "bg-amber-100 text-amber-700", icon: Clock };
    if (s === "skipped") return { cls: "bg-[#9ac7f7]/20 text-[#447694]", icon: Ban };
    if (s === "failed") return { cls: "bg-red-100 text-red-700", icon: AlertTriangle };
    return { cls: "bg-[#9ac7f7]/20 text-[#447694]", icon: Clock };
  };

  const fmtDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleString("en-GB"); } catch { return iso; }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-email-drips-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="bnp-chip bnp-chip-light">Email drip queue</span>
          <h1 className="font-display text-3xl md:text-4xl text-[#04273d] mt-2 font-semibold tracking-tight">Follow-up emails</h1>
          <p className="text-sm text-[#447694] mt-1">
            Day-2 and Day-5 nudges scheduled after a free name check.
            {" "}{counts.pending} pending &middot; {counts.sent} sent &middot; {counts.skipped} skipped &middot; {counts.failed} failed
          </p>
        </div>
        <button
          onClick={load}
          className="btn-secondary text-sm"
          disabled={loading}
          data-testid="admin-email-drips-refresh-button"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Filter chips */}
      <div className="mt-6 flex items-center gap-2 flex-wrap">
        {["all", "pending", "sent", "skipped", "failed"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-display font-semibold capitalize transition-colors ${
              statusFilter === s
                ? "bg-[#04273d] text-white"
                : "bg-white text-[#447694] border border-[#9ac7f7]/40 hover:border-[#04273d]"
            }`}
            data-testid={`admin-email-drips-filter-${s}`}
          >
            {s}
          </button>
        ))}
        <div className="relative ml-auto max-w-xs w-full">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#447694]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email, business, kind"
            className="bnp-input pl-10 text-sm"
            data-testid="admin-email-drips-search"
          />
        </div>
      </div>

      <div className="bnp-card mt-5 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-[#447694]" data-testid="admin-email-drips-loading">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center" data-testid="admin-email-drips-empty">
            <Mail size={32} className="text-[#9ac7f7] mx-auto" />
            <p className="font-display font-semibold text-[#04273d] mt-3">No drip emails {statusFilter !== "all" ? `(${statusFilter})` : "queued"}</p>
            <p className="text-sm text-[#447694] mt-1">
              Drips are scheduled automatically when someone downloads their name-check PDF report.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-email-drips-table">
              <thead className="bg-[#f7f7f7] text-[#447694] text-left">
                <tr>
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Business</th>
                  <th className="px-5 py-3 font-medium">Kind</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Send after</th>
                  <th className="px-5 py-3 font-medium">Scheduled</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => {
                  const b = statusBadge(d.status);
                  const Icon = b.icon;
                  return (
                    <tr key={d.id} className="border-t border-[#9ac7f7]/20" data-testid={`admin-email-drip-row-${d.id}`}>
                      <td className="px-5 py-3 text-[#04273d] font-medium">{d.lead_email || "-"}</td>
                      <td className="px-5 py-3 text-[#447694]">{d.business_name || "-"}</td>
                      <td className="px-5 py-3">
                        <span className="bnp-chip bnp-chip-light text-[10px] uppercase">{d.kind || "-"}</span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-display font-semibold capitalize ${b.cls}`}>
                          <Icon size={11} /> {d.status || "unknown"}
                        </span>
                        {d.skipped_reason && (
                          <span className="block text-[10px] text-[#447694] mt-1">{d.skipped_reason}</span>
                        )}
                        {d.error && (
                          <span className="block text-[10px] text-red-600 mt-1 max-w-[14rem] truncate" title={d.error}>{d.error}</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-[#447694] font-mono-brand text-xs">{fmtDate(d.send_after)}</td>
                      <td className="px-5 py-3 text-[#447694] font-mono-brand text-xs">{fmtDate(d.scheduled_at)}</td>
                      <td className="px-5 py-3 text-right">
                        {d.status === "pending" && (
                          <button
                            onClick={() => cancelDrip(d.id, d.lead_email)}
                            className="inline-flex items-center gap-1 text-[11.5px] text-red-600 hover:underline"
                            data-testid={`admin-email-drip-cancel-${d.id}`}
                          >
                            <XCircle size={11} /> Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
